/**
 * shippingUtils.js
 *
 * Calculates shipping charge based on customer delivery pincode.
 * Pickup origin: 394107 (Surat, Gujarat)
 *
 * Zones (all charges are GST-inclusive at 18%):
 *   Local  — 394xxx (Surat district)        → ₹0   (Free)
 *   Zone A — Rest of Gujarat (36-39 prefix)  → ₹49
 *   Zone B — Maharashtra, Rajasthan, MP/CG   → ₹79
 *   Zone C — North India (HP, J&K, Delhi…)   → ₹99
 *   Zone D — South India (AP, TN, Kerala…)   → ₹119
 *   Zone E — East / NE India                 → ₹139
 *   Zone F — Unknown / invalid pincode        → ₹99  (default)
 */

const SHIPPING_ZONES = [
    {
        label: "Free Delivery (Surat District)",
        charge: 0,
        isFree: true,
        test: (pin) => pin >= 394000 && pin <= 394999,
    },
    {
        label: "Gujarat Delivery",
        charge: 49,
        isFree: false,
        test: (pin) => pin >= 360000 && pin <= 399999,
    },
    {
        label: "West / Central India",
        charge: 79,
        isFree: false,
        // Maharashtra (400-445), Goa (403), Rajasthan (300-342), MP/CG (450-497)
        test: (pin) =>
            (pin >= 400000 && pin <= 445999) ||
            (pin >= 300000 && pin <= 342999) ||
            (pin >= 450000 && pin <= 497999),
    },
    {
        label: "North India",
        charge: 99,
        isFree: false,
        // Delhi (110), Haryana (121-136), Punjab (140-160), HP (171-177),
        // J&K (180-194), UP (200-285), Uttarakhand (246-263)
        test: (pin) => pin >= 100000 && pin <= 299999,
    },
    {
        label: "South India",
        charge: 119,
        isFree: false,
        // AP/Telangana (500-535), Karnataka (560-591), Kerala (670-695), TN (600-643)
        test: (pin) => pin >= 500000 && pin <= 699999,
    },
    {
        label: "East / North-East India",
        charge: 139,
        isFree: false,
        // WB (700-743), Odisha (751-770), Bihar/Jharkhand (800-835),
        // Assam/NE (781-799), Sikkim (737)
        test: (pin) => pin >= 700000 && pin <= 899999,
    },
];

const DEFAULT_CHARGE = { label: "Standard Delivery", charge: 99, isFree: false };

/**
 * Extract the first 6-digit number from an address string.
 * @param {string} address - Full address text entered by the customer
 * @returns {number|null} - Parsed pincode or null
 */
export const extractPincodeFromAddress = (address) => {
    if (!address) return null;
    const match = address.match(/\b(\d{6})\b/);
    return match ? parseInt(match[1], 10) : null;
};

/**
 * Get the shipping charge info for a given customer address.
 * @param {string} address - Full address string (pincode must be in it)
 * @returns {{ charge: number, label: string, isFree: boolean, pincode: number|null }}
 */
export const getShippingCharge = (address) => {
    const pincode = extractPincodeFromAddress(address);

    if (!pincode) {
        return { ...DEFAULT_CHARGE, pincode: null };
    }

    for (const zone of SHIPPING_ZONES) {
        if (zone.test(pincode)) {
            return { charge: zone.charge, label: zone.label, isFree: zone.isFree, pincode };
        }
    }

    return { ...DEFAULT_CHARGE, pincode };
};

/**
 * Convenience: returns just the numeric charge.
 * @param {string} address
 * @returns {number}
 */
export const getShippingChargeAmount = (address) => getShippingCharge(address).charge;
